import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getHost, supportsHostAuthToken } from '@repo/host/host';
import type { UserAccount } from '@repo/types/account';
import { logoutAction } from '@repo/wemod-api-client/actions/auth-actions';
import { setOnTokenExpired, setTokenOverride } from '@repo/wemod-api-client/api-client';
import { TOKEN_COOKIE_NAME } from '@repo/wemod-api-client/auth/constants';
import { refreshTokenWithTimeout } from '@repo/wemod-api-client/auth/refresh-token-with-timeout';
import { getAccount, userLogin } from '@repo/wemod-api-client/auth-api-client';
import { readClientCookie } from '@repo/wemod-api-client/utils/cookie-utils';

const customBaseQuery = fetchBaseQuery({
    baseUrl: '/', // Not used since we're using custom queryFn
});

export interface LoginCredentials {
    emailOrUsername: string;
    password: string;
    turnstileToken: string;
}

function getAuthTokenFromState(state: unknown): string | null {
    if (typeof state !== 'object' || state === null || !('auth' in state)) {
        return null;
    }

    const { auth } = state;
    if (typeof auth !== 'object' || auth === null || !('token' in auth)) {
        return null;
    }

    return typeof auth.token === 'string' ? auth.token : null;
}

function canUseRpcAuthToken(host: ReturnType<typeof getHost>): boolean {
    return supportsHostAuthToken(host.info);
}

export const authApi = createApi({
    reducerPath: 'authApi',
    baseQuery: customBaseQuery,
    tagTypes: ['User', 'Auth'],
    endpoints: builder => ({
        getUser: builder.query<{ user: UserAccount; token: string }, void>({
            queryFn: async (_, { getState }) => {
                try {
                    let bearerToken = readClientCookie(TOKEN_COOKIE_NAME);
                    let usingStateToken = false;
                    let gotRpcToken = false;

                    if (!bearerToken) {
                        bearerToken = getAuthTokenFromState(getState());
                        usingStateToken = !!bearerToken;
                    }

                    const host = getHost();
                    if (canUseRpcAuthToken(host)) {
                        try {
                            const rpcToken = await host.getAuthToken();
                            if (rpcToken) {
                                bearerToken = rpcToken;
                                usingStateToken = false;
                                gotRpcToken = true;
                            }
                        } catch {
                            // RPC failed, fall through to session exchange
                        }
                    }

                    if (!bearerToken) {
                        if (canUseRpcAuthToken(host)) {
                            return { error: { status: 'FETCH_ERROR', error: 'No host auth token' } };
                        }

                        const result = await refreshTokenWithTimeout();
                        if (!result.success) {
                            return { error: { status: 'FETCH_ERROR', error: result.error } };
                        }
                        bearerToken = result.token;
                    }

                    if (!bearerToken || typeof bearerToken !== 'string') {
                        return { error: { status: 'FETCH_ERROR', error: 'Bearer token is missing or invalid' } };
                    }

                    const userData = await getAccount(bearerToken);
                    if (!userData) {
                        return { error: { status: 'FETCH_ERROR', error: 'Failed to fetch user account' } };
                    }

                    if (usingStateToken) {
                        setTokenOverride(bearerToken);
                    }

                    if (gotRpcToken) {
                        setTokenOverride(bearerToken);
                        setOnTokenExpired(async () => {
                            try {
                                const host = getHost();
                                const token = await host.getAuthToken();
                                return token || null;
                            } catch {
                                return null;
                            }
                        });
                    }

                    return { data: { user: userData, token: bearerToken } };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            providesTags: ['User', 'Auth'],
        }),

        login: builder.mutation<{ user: UserAccount; token: string }, LoginCredentials>({
            queryFn: async ({ emailOrUsername, password, turnstileToken }) => {
                try {
                    const response = await userLogin(emailOrUsername, password, turnstileToken);

                    if (!response) {
                        return { error: { status: 'FETCH_ERROR', error: 'Failed to login' } };
                    }

                    if (!response.ok) {
                        return { error: { status: 'FETCH_ERROR', error: 'Failed to login' } };
                    }

                    const json = await response.json();
                    const bearerToken = json.accessToken;

                    const userData = await getAccount(bearerToken);
                    if (!userData) {
                        return { error: { status: 'FETCH_ERROR', error: 'Failed to fetch user account after login' } };
                    }

                    return { data: { user: userData, token: bearerToken } };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: ['User', 'Auth'],
        }),

        logout: builder.mutation<void, void>({
            queryFn: async () => {
                try {
                    await logoutAction();
                    setTokenOverride(null);
                    setOnTokenExpired(null);

                    return { data: undefined };
                } catch (error) {
                    return { error: { status: 'FETCH_ERROR', error: (error as Error).message } };
                }
            },
            invalidatesTags: ['User', 'Auth'],
        }),
    }),
});

export const { useGetUserQuery, useLoginMutation, useLogoutMutation } = authApi;
